/**
 * RotaCalendar — single-entity weekly calendar (Teams/Outlook style).
 *
 * Shows ONE Service User (or ONE worker) per screen. Above the grid is a
 * carousel of pickers (with prev/next arrows) so RM can rotate through the
 * roster without leaving the calendar. No vertical scroll over many rows.
 *
 *  - 7 day columns (Mon..Sun of the chosen week)
 *  - 30-min time slots from 06:00 to 23:30 on the left rail
 *  - Click any slot in any day → open the shift form pre-populated with the
 *    selected entity, that date and that start time (end defaults to +1 h).
 *  - Existing shifts overlay as colored blocks at their proportional position.
 *  - Overnight (sleep-in) shifts also paint a dashed tail block on the
 *    following day, so the rollover is visible.
 */

import { useMemo, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, CalendarPlus, Users, UserCircle2, Search, CopyPlus, Repeat } from "lucide-react";
import { fmtDate, fmtISODate } from "@/lib/dates";

const SLOT_MIN = 30;       // minutes per slot
const SLOT_PX = 28;        // visual height of one 30-min slot
const DAY_START = 6;       // 06:00 first visible slot
const DAY_END = 23;        // 23:00 last visible slot (allows up to 23:30)
const SLOTS_PER_DAY = (DAY_END - DAY_START + 1) * 2; // 36 half-hour blocks
const DAYS = 7;

const pad2 = (n) => String(n).padStart(2, "0");
const slotToTime = (idx) => {
  const m = DAY_START * 60 + idx * SLOT_MIN;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
};
const timeToMinutes = (t) => {
  const [h, m] = (t || "00:00").split(":").map(Number);
  return h * 60 + m;
};
const plusOneHour = (t) => {
  const m = timeToMinutes(t) + 60;
  return `${pad2(Math.floor(m / 60) % 24)}:${pad2(m % 60)}`;
};
const buildWeek = (weekStartIso) => {
  const start = new Date(`${weekStartIso}T12:00:00Z`);
  return Array.from({ length: DAYS }).map((_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
};

function shiftsForCell(shifts, pivotId, dateIso, pivot) {
  const out = [];
  for (const sh of shifts) {
    if (sh.status === "cancelled" || sh.status === "declined") continue;
    const matchesRow =
      pivot === "worker"
        ? sh.worker_id === pivotId
        : (sh.visit_slots || []).some((s) => s.service_user_id === pivotId);
    if (!matchesRow) continue;
    if (sh.date === dateIso) {
      out.push({ ...sh, _segment: "primary" });
    } else if (sh.overnight) {
      const d = new Date(`${sh.date}T12:00:00Z`);
      d.setUTCDate(d.getUTCDate() + 1);
      const tailDate = d.toISOString().slice(0, 10);
      if (tailDate === dateIso) out.push({ ...sh, _segment: "tail" });
    }
  }
  return out;
}

function shiftBlockGeom(sh) {
  const startMin = sh._segment === "primary" ? timeToMinutes(sh.start_time) : 0;
  let endMin =
    sh._segment === "primary"
      ? sh.overnight ? 24 * 60 : timeToMinutes(sh.end_time)
      : timeToMinutes(sh.end_time);
  if (endMin <= startMin && !sh.overnight) endMin = startMin + SLOT_MIN;
  const dayStartMin = DAY_START * 60;
  const top = Math.max(0, (startMin - dayStartMin) / SLOT_MIN) * SLOT_PX;
  const bottom = Math.max(0, (endMin - dayStartMin) / SLOT_MIN) * SLOT_PX;
  return {
    top,
    height: Math.max(SLOT_PX, bottom - top),
    label: `${sh.start_time}–${sh.end_time}${sh.overnight ? " (overnight)" : ""}`,
  };
}

function workerOptions(staff = []) {
  return staff
    .filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role))
    .map((s) => ({ id: s.id, label: s.full_name, sub: (s.role || "").replace(/_/g, " ") }));
}
function suOptions(users = []) {
  return users.map((u) => ({ id: u.id, label: u.full_name, sub: u.address?.line1 || "" }));
}

export default function RotaCalendar({
  shifts,
  staff,
  users,
  defaultPivot = "service_user",
  defaultEntityId = null,
  onSlotClick,
  onShiftClick,
  onShiftDrop,
  onDuplicateWeek,
  onBulkAdd,
  weekStart,
  onWeekChange,
}) {
  const [pivot, setPivot] = useState(defaultPivot);
  const [filter, setFilter] = useState("");

  const entities = useMemo(
    () => (pivot === "worker" ? workerOptions(staff) : suOptions(users)),
    [pivot, staff, users]
  );
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter((e) => e.label.toLowerCase().includes(q) || (e.sub || "").toLowerCase().includes(q));
  }, [entities, filter]);

  const [selectedId, setSelectedId] = useState(defaultEntityId || (entities[0] && entities[0].id) || null);

  // Keep selection valid as pivot / data changes.
  useEffect(() => {
    if (!entities.length) { setSelectedId(null); return; }
    if (!entities.find((e) => e.id === selectedId)) setSelectedId(entities[0].id);
  }, [entities, selectedId]);

  const selected = entities.find((e) => e.id === selectedId) || null;
  const days = useMemo(() => buildWeek(weekStart), [weekStart]);

  const cycleEntity = (delta) => {
    if (!entities.length) return;
    const i = Math.max(0, entities.findIndex((e) => e.id === selectedId));
    const next = entities[(i + delta + entities.length) % entities.length];
    setSelectedId(next.id);
  };

  const stepWeek = (delta) => {
    const d = new Date(`${weekStart}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + delta * 7);
    onWeekChange(d.toISOString().slice(0, 10));
  };

  const handleSlotClick = (dateIso, slotIdx) => {
    if (!selected || !onSlotClick) return;
    const start = slotToTime(slotIdx);
    const end = plusOneHour(start);
    onSlotClick({
      pivot,
      lockedEntity: selected,
      lockedDate: dateIso,
      initialStart: start,
      initialEnd: end,
    });
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="rota-calendar">
      {/* Toolbar — pivot toggle, week navigation */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
        <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 p-0.5" data-testid="rota-calendar-pivot">
          <button
            onClick={() => setPivot("service_user")}
            data-testid="rota-calendar-pivot-su"
            className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 ${pivot === "service_user" ? "bg-[#0D7377] text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <UserCircle2 className="h-3.5 w-3.5" /> Service Users
          </button>
          <button
            onClick={() => setPivot("worker")}
            data-testid="rota-calendar-pivot-worker"
            className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 ${pivot === "worker" ? "bg-[#0D7377] text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <Users className="h-3.5 w-3.5" /> Workers
          </button>
        </div>
        <div className="inline-flex items-center gap-1.5">
          {onBulkAdd && (
            <button
              onClick={onBulkAdd}
              data-testid="rota-calendar-bulk-add"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50"
              title="Create a recurring shift pattern (e.g. Mon/Wed/Fri 09:00–13:00 across a date range)"
            >
              <Repeat className="h-3.5 w-3.5" /> Bulk add
            </button>
          )}
          {onDuplicateWeek && (
            <button
              onClick={() => onDuplicateWeek(weekStart)}
              data-testid="rota-calendar-duplicate-week"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50"
              title="Copy every shift in this week forward to the next week"
            >
              <CopyPlus className="h-3.5 w-3.5" /> Duplicate week
            </button>
          )}
          <button onClick={() => stepWeek(-1)} data-testid="rota-calendar-prev" className="p-1.5 rounded-md hover:bg-slate-100" aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></button>
          <p className="text-sm font-semibold text-slate-900 px-2 min-w-[200px] text-center" data-testid="rota-calendar-week-label">
            {fmtDate(days[0])} – {fmtDate(days[6])}
          </p>
          <button onClick={() => stepWeek(1)} data-testid="rota-calendar-next" className="p-1.5 rounded-md hover:bg-slate-100" aria-label="Next week"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={() => onWeekChange(fmtISODate(new Date()))} data-testid="rota-calendar-today" className="ml-2 text-xs px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-50">This week</button>
        </div>
      </div>

      {/* Entity selector — rotate between SUs / workers without scrolling */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1.5">
            <button
              onClick={() => cycleEntity(-1)}
              disabled={!entities.length}
              data-testid="rota-calendar-entity-prev"
              className="p-1.5 rounded-md border border-slate-300 hover:bg-white disabled:opacity-50"
              aria-label="Previous entity"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-[260px]" data-testid="rota-calendar-entity-current">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">{pivot === "worker" ? "Worker" : "Service User"}</p>
              <p className="font-semibold text-slate-900 leading-tight truncate">{selected?.label || "—"}</p>
              <p className="text-[11px] text-slate-500 truncate">{selected?.sub || ""}</p>
            </div>
            <button
              onClick={() => cycleEntity(1)}
              disabled={!entities.length}
              data-testid="rota-calendar-entity-next"
              className="p-1.5 rounded-md border border-slate-300 hover:bg-white disabled:opacity-50"
              aria-label="Next entity"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`Search ${pivot === "worker" ? "workers" : "service users"}…`}
                data-testid="rota-calendar-search"
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-300 rounded-md bg-white"
              />
            </div>
            <select
              value={selectedId || ""}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="rota-calendar-entity-select"
              className="px-2 py-1.5 text-sm border border-slate-300 rounded-md bg-white max-w-[220px]"
            >
              {filtered.map((e) => (
                <option key={e.id} value={e.id}>{e.label}</option>
              ))}
              {filtered.length === 0 && <option disabled>No matches</option>}
            </select>
          </div>
        </div>
      </div>

      {/* Weekly grid for the selected entity */}
      {!selected ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="rota-calendar-empty">
          No {pivot === "worker" ? "workers" : "service users"} yet.
        </div>
      ) : (
        <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 320px)" }}>
          <div className="min-w-[980px]">
            {/* Day header row */}
            <div className="grid grid-cols-[64px_repeat(7,1fr)] sticky top-0 z-10 bg-white border-b border-slate-200">
              <div className="px-2 py-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500 border-r border-slate-200">Time</div>
              {days.map((d) => (
                <div key={d} className="px-2 py-2 text-center border-r border-slate-100 last:border-r-0">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">
                    {new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "Europe/London" }).format(new Date(`${d}T12:00:00Z`))}
                  </p>
                  <p className="text-sm font-semibold text-slate-900">{fmtDate(d).slice(0, 6)}</p>
                </div>
              ))}
            </div>

            {/* Time rail + per-day stacks (a single big grid: 36 rows × 7 cols) */}
            <div className="grid grid-cols-[64px_repeat(7,1fr)] relative" style={{ height: SLOTS_PER_DAY * SLOT_PX }}>
              {/* Left time labels — show every full hour */}
              <div className="border-r border-slate-200 bg-slate-50/40 relative">
                {Array.from({ length: SLOTS_PER_DAY }).map((_, idx) => (
                  idx % 2 === 0 ? (
                    <div
                      key={idx}
                      className="absolute left-0 right-0 text-[10px] text-slate-500 pl-1 border-t border-slate-100"
                      style={{ top: idx * SLOT_PX, height: SLOT_PX * 2 }}
                    >
                      {slotToTime(idx)}
                    </div>
                  ) : null
                ))}
              </div>

              {/* Day columns */}
              {days.map((dateIso) => {
                const cellShifts = shiftsForCell(shifts, selected.id, dateIso, pivot);
                return (
                  <div key={dateIso} className="relative border-r border-slate-100 last:border-r-0" data-testid={`rota-calendar-cell-${dateIso}`}>
                    {Array.from({ length: SLOTS_PER_DAY }).map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSlotClick(dateIso, idx)}
                        onDragOver={(e) => { if (onShiftDrop) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                        onDrop={(e) => {
                          if (!onShiftDrop) return;
                          e.preventDefault();
                          const shiftId = e.dataTransfer.getData("text/shift-id");
                          if (shiftId) onShiftDrop({ shiftId, newDate: dateIso, newStart: slotToTime(idx) });
                        }}
                        data-testid={`rota-calendar-slot-${dateIso}-${idx}`}
                        className={`block w-full text-left hover:bg-emerald-50/70 group ${idx % 2 === 0 ? "border-t border-slate-100" : "border-t border-dashed border-slate-100"}`}
                        style={{ height: SLOT_PX }}
                        title={`Click to add shift at ${slotToTime(idx)}`}
                      />
                    ))}
                    {cellShifts.map((sh) => {
                      const g = shiftBlockGeom(sh);
                      const isTail = sh._segment === "tail";
                      return (
                        <div
                          key={`${sh.id}-${sh._segment}`}
                          data-testid={`rota-calendar-shift-${sh.id}-${sh._segment}`}
                          draggable={!isTail && !!onShiftDrop}
                          onDragStart={(e) => {
                            if (isTail || !onShiftDrop) return;
                            e.dataTransfer.setData("text/shift-id", sh.id);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onClick={(e) => {
                            if (isTail || !onShiftClick) return;
                            e.stopPropagation();
                            onShiftClick(sh);
                          }}
                          role={!isTail && onShiftClick ? "button" : undefined}
                          tabIndex={!isTail && onShiftClick ? 0 : undefined}
                          onKeyDown={(e) => {
                            if (isTail || !onShiftClick) return;
                            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onShiftClick(sh); }
                          }}
                          className={`absolute left-1 right-1 rounded px-1.5 py-1 text-[11px] overflow-hidden shadow-sm border ${
                            sh.status === "published"
                              ? "bg-[#0D7377] text-white border-[#0A5C5F]"
                              : "bg-amber-100 text-amber-900 border-amber-300"
                          } ${isTail ? "opacity-80 border-dashed cursor-default" : "cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-emerald-400"}`}
                          style={{ top: g.top, height: g.height }}
                          title={isTail ? g.label : `${g.label} · click to edit, drag to move`}
                        >
                          <p className="font-semibold truncate">{g.label}</p>
                          <p className="text-[10px] opacity-90 truncate">
                            {pivot === "worker"
                              ? `${(sh.visit_slots || []).length} visit(s)`
                              : (sh.worker_name || "Unassigned")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500 inline-flex items-center gap-2">
        <CalendarPlus className="h-3.5 w-3.5" />
        Click any empty slot to add a shift starting at that time (end defaults to +1 h, fully editable).
      </div>
    </div>
  );
}
